import { useState, useRef, useCallback, useMemo, useEffect } from 'react';

// #region ヘルパ関数

/**
 * 正規表現の特殊文字をエスケープする
 *
 * @param {string} string
 * @returns {string}
 */
const escapeRegExp = (string) =>
  string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * キーワードから柔軟マッチ用の正規表現パターン文字列を生成する
 *
 * 各文字間に [\s　]* を挿入することで、PDF テキスト抽出時に
 * フォント情報由来で挿入された不当な空白・全角スペースを吸収する。
 * keyword が空白のみの場合は何にもマッチしないパターンを返す。
 *
 * @param {string} keyword
 * @returns {string} 正規表現パターン文字列
 */
const buildFlexPattern = (keyword) => {
  const normalized = keyword.replace(/[\s　]+/g, '');
  if (!normalized) return '(?!)';
  return normalized.split('').map(c => escapeRegExp(c)).join('[\\s\\u3000]*');
};

/**
 * HTML エスケープを行う
 *
 * @param {string} s
 * @returns {string}
 */
const escapeHtml = (s) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

/**
 * 検索クエリをスペース区切りでキーワード配列に分割する
 *
 * @param {string} query
 * @returns {string[]}
 */
const getSearchKeywords = (query) =>
  query.trim().split(/\s+/).filter(Boolean);

/**
 * getTextContent の items 配列からオフセットマップを構築する
 *
 * スペースなしで全アイテムを連結し、各アイテムの文字位置範囲を記録する。
 *
 * @param {Array<{str: string}>} items
 * @returns {Array<{str: string, start: number, end: number}>}
 */
const buildTextMap = (items) => {
  let offset = 0;
  return items.map((item) => {
    const entry = { str: item.str, start: offset, end: offset + item.str.length };
    offset += item.str.length;
    return entry;
  });
};

/**
 * 連結文字列上でキーワードを検索し、itemIndex → ハイライト範囲[] のマップを返す
 *
 * アイテムをまたぐキーワードは各アイテムに部分範囲として記録される。
 * 各範囲には resultIndex（ページ内マッチ順 0 始まり）を付与し、
 * クロスアイテムのマークが同一 resultIndex を共有できるようにする。
 *
 * @param {Array<{str: string, start: number, end: number}>} textMap
 * @param {string[]} keywords
 * @returns {Object.<number, Array<{start: number, end: number, resultIndex: number}>>}
 */
const buildItemHighlights = (textMap, keywords) => {
  if (!textMap.length || !keywords.length) return {};

  const fullText = textMap.map((t) => t.str).join('');

  // 全キーワードのマッチを収集してから位置昇順でソートし resultIndex を確定する
  // （キーワード処理順ではなく位置順が findCurrentResultDomIndex と一致する）
  const allMatches = [];
  for (const keyword of keywords) {
    const regex = new RegExp(buildFlexPattern(keyword), 'gi');
    for (const match of fullText.matchAll(regex)) {
      allMatches.push({ start: match.index, end: match.index + match[0].length });
    }
  }
  allMatches.sort((a, b) => a.start - b.start);

  const result = {};
  allMatches.forEach(({ start: matchStart, end: matchEnd }, resultIndex) => {
    textMap.forEach(({ start, end }, i) => {
      const overlapStart = Math.max(matchStart, start);
      const overlapEnd = Math.min(matchEnd, end);
      if (overlapStart >= overlapEnd) return;
      (result[i] ??= []).push({ start: overlapStart - start, end: overlapEnd - start, resultIndex });
    });
  });
  return result;
};

/**
 * str の指定範囲に <mark> を適用した HTML 文字列を返す
 *
 * data-search-result-index 属性を付与することで、クロスアイテムのマーク群を
 * 同一の resultIndex でまとめて current ハイライト対象にできる。
 *
 * @param {string} str
 * @param {Array<{start: number, end: number, resultIndex: number}>|undefined} ranges
 * @returns {string}
 */
const applyRangeHighlights = (str, ranges) => {
  if (!ranges?.length) return escapeHtml(str);
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  let result = '';
  let cursor = 0;
  for (const { start, end, resultIndex } of sorted) {
    if (end <= cursor) continue;
    const effectiveStart = Math.max(start, cursor);
    result += escapeHtml(str.slice(cursor, effectiveStart));
    result += `<mark class="pdf-search-highlight" data-search-result-index="${resultIndex}">${escapeHtml(str.slice(effectiveStart, end))}</mark>`;
    cursor = end;
  }
  return result + escapeHtml(str.slice(cursor));
};

/**
 * PDF ページオブジェクトからページ全文の文字列を生成する
 *
 * @param {import('pdfjs-dist').PDFPageProxy} page
 * @returns {Promise<{ pageText: string, lowerPageText: string, itemMap: Array<{start: number, end: number, x: number, y: number}> }>}
 */
const extractPageText = async (page) => {
  const textContent = await page.getTextContent();
  let pageText = '';
  // 各テキストアイテムの文字位置範囲と PDF 座標を保持する
  // x,y 座標はソートのために使用する (item.transform = [scaleX, skewX, skewY, scaleY, x, y])
  const itemMap = [];

  for (const item of textContent.items) {
    const start = pageText.length;
    pageText += item.str;
    itemMap.push({ start, end: pageText.length, x: item.transform[4], y: item.transform[5] });
  }

  // 大文字小文字を区別しない検索のため、ページテキストの小文字版も生成して返す
  return { pageText, lowerPageText: pageText.toLowerCase(), itemMap };
};

/**
 * 1 ページのテキスト内でキーワードが出現する全位置を収集する
 *
 * @param {{ pageText: string, lowerPageText: string, pageNum: number, keywords: string[], itemMap: Array<{start: number, end: number, x: number, y: number}> }} params
 * @returns {Array<{ pageNum: number, position: number, x: number, y: number, term: string, context: string }>}
 */
const findKeywordPositionsInText = ({ pageText, lowerPageText, pageNum, keywords, itemMap }) => {
  const results = [];

  // 各キーワードについて、ページテキスト内の全出現位置を収集する
  for (const keyword of keywords) {
    // 柔軟パターンで検索し、PDF 内の不当な空白を吸収する（大文字小文字は lowerPageText 側で吸収済み）
    const regex = new RegExp(buildFlexPattern(keyword.toLowerCase()), 'g');
    for (const match of lowerPageText.matchAll(regex)) {
      const startIndex = match.index;
      const matchLength = match[0].length;
      // マッチ位置が属するテキストアイテムの x,y 座標を取得する（視覚順ソートに使用）
      const item = itemMap.find(i => i.start <= startIndex && startIndex < i.end);
      results.push({
        pageNum,
        position: startIndex,
        x: item?.x ?? 0,
        y: item?.y ?? 0,
        term: keyword,
        context: pageText.substring(
          Math.max(0, startIndex - 30),
          Math.min(pageText.length, startIndex + matchLength + 30),
        ),
      });
    }
  }
  return results;
};

/**
 * 検索結果をページ番号 → 視覚順 (y 降順 → x 昇順) でソートする
 *
 * PDF の y 座標は左下原点のため、y が大きいほど画面上方に位置する。
 * 同一行とみなす閾値は 1pt（浮動小数点誤差を吸収）。
 *
 * @param {Array<{ pageNum: number, x: number, y: number }>} results
 * @returns {Array}
 */
const sortSearchResults = (results) =>
  [...results].sort((a, b) => {
    if (a.pageNum !== b.pageNum) return a.pageNum - b.pageNum;
    // y 降順: PDF y=0 は底辺なので大きい方が画面上方
    if (Math.abs(a.y - b.y) > 1) return b.y - a.y;
    // 同一行内は x 昇順（左から右）
    return a.x - b.x;
  });

/**
 * 全ページを走査してキーワードにマッチする位置を収集し、ソート済みで返す
 *
 * @param {import('pdfjs-dist').PDFDocumentProxy} pdfDocument
 * @param {string[]} keywords
 * @returns {Promise<{ results: Array<{ pageNum: number, position: number, x: number, y: number, term: string, context: string }>, hasAnyText: boolean }>}
 */
const collectAllPageResults = async (pdfDocument, keywords) => {
  const results = [];
  let hasAnyText = false;

  // 全ページを走査してキーワードのヒット位置を収集する
  for (let pageNum = 1; pageNum <= pdfDocument.numPages; pageNum++) {
    const page = await pdfDocument.getPage(pageNum);
    const { pageText, lowerPageText, itemMap } = await extractPageText(page);
    if (pageText.trim().length > 0) hasAnyText = true;
    const pageResults = findKeywordPositionsInText({ pageText, lowerPageText, pageNum, keywords, itemMap });
    results.push(...pageResults);
  }

  // ページ番号 → 視覚順 (y 降順 → x 昇順) でソートし、画面上の読み取り順に揃える
  return { results: sortSearchResults(results), hasAnyText };
};

/**
 * 現在ページ以降で最初にヒットした結果のインデックスを返す
 *
 * @param {Array<{ pageNum: number }>} results - ソート済み検索結果
 * @param {number} currentPage
 * @returns {number} 初期選択インデックス（results が空の場合は -1、見つからない場合は 0）
 */
const selectFirstSearchIndex = (results, currentPage) => {
  // ヒットなしの場合は -1 を返す
  if (results.length === 0) return -1;

  // 現在ページ以降で最初にヒットした結果を探す
  const startIdx = results.findIndex((r) => r.pageNum >= currentPage);

  // 見つからない場合は先頭の結果を返す
  return startIdx !== -1 ? startIdx : 0;
};

/**
 * 現在選択中の検索結果について、現在ページ内での DOM 順インデックスを返す。
 *
 * 選択結果が現在ページ以外にある場合（DOM が存在しないページ）は -1 を返す。
 * PDF.js のテキストレイヤーは x,y 座標順ではなく DOM 要素順で要素を返すため、
 * position（DOM 要素順に対応したテキスト位置情報）でソートしてインデックスを求める。
 *
 * @param {Array<{ pageNum: number, position: number }>} searchResults
 * @param {number} currentSearchIndex
 * @param {number} currentPage
 * @returns {number} DOM 順インデックス（別ページ・範囲外・未検出の場合は -1）
 */
const findCurrentResultDomIndex = (searchResults, currentSearchIndex, currentPage) => {

  // フォーカス対象の結果が、undefined（インデックス範囲外）または別ページの場合は -1 を返す
  const currentResult = searchResults[currentSearchIndex];
  if (!currentResult || currentResult.pageNum !== currentPage) return -1;

  //　現在ページの結果を position（DOM要素順に対応したテキスト位置情報）でソート
  const pageResults = searchResults.filter(r => r.pageNum === currentPage);
  const pageResultsByDomOrder = [...pageResults].sort((a, b) => a.position - b.position);
  
  // DOM 要素順基準で、フォーカス対象テキストのインデックスを返す
  return pageResultsByDomOrder.findIndex(r => r === currentResult);
};

// #endregion

// #region カスタムフック

/**
 * PDFキーワード検索を管理するカスタムフック
 *
 * スペース区切りで複数のキーワードをOR検索し、全ページを対象に検索結果を収集する。
 * ハイライトは react-pdf の customTextRenderer を利用してテキストレイヤーに直接描画する。
 * DOM操作（innerHTML / setTimeout）を使わないため、ズーム変更後も自動的に再適用される。
 *
 * @param {Object} options
 * @param {import('pdfjs-dist').PDFDocumentProxy|null} options.pdfDocument - PDFDocumentProxy（react-pdf が提供）
 * @param {number} options.currentPage - 現在のページ番号
 * @param {Function} options.setCurrentPage - ページ番号更新関数
 * @param {React.RefObject} [options.containerRef] - mark 要素の検索スコープを限定するコンテナの ref（省略時は document 全体）
 *
 * @returns {Object}
 * @returns {string}   returns.searchText            - 検索入力テキスト
 * @returns {Function} returns.setSearchText         - 検索テキスト更新関数
 * @returns {Array<{pageNum: number, position: number, term: string, context: string}>}
 *                     returns.searchResults         - 全ページの検索ヒット一覧
 * @returns {number}   returns.currentSearchIndex    - 現在フォーカス中の結果インデックス（-1 = 未選択）
 * @returns {boolean}  returns.isSearching           - 検索処理の実行中フラグ
 * @returns {string|null} returns.searchError        - 検索エラーメッセージ（エラーなし時は null）
 * @returns {Function} returns.handleSearch          - 検索実行ハンドラ
 * @returns {Function} returns.handleNextSearchResult - 次の検索結果へ移動ハンドラ
 * @returns {Function} returns.handlePrevSearchResult - 前の検索結果へ移動ハンドラ
 * @returns {Function} returns.handleClearSearch     - 検索クリアハンドラ
 * @returns {Function} returns.customTextRenderer          - react-pdf Page コンポーネントに渡すテキストレンダラー
 * @returns {Function} returns.onGetTextSuccess            - テキストコンテンツ取得完了時に呼ぶコールバック（react-pdf Page の onGetTextSuccess に渡す）
 * @returns {Function} returns.onTextLayerRenderSuccess    - テキストレイヤー描画完了時に呼ぶコールバック（react-pdf Page の onRenderTextLayerSuccess に渡す）
 */
export const usePdfKeywordSearch = ({ pdfDocument, currentPage, setCurrentPage, containerRef }) => {
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  // テキストレイヤーの描画完了をハイライト effect に通知するためのカウンタ
  const [textLayerVersion, setTextLayerVersion] = useState(0);
  // 現在レンダリング中のページのテキストマップ（onGetTextSuccess で更新）
  const [currentPageTextMap, setCurrentPageTextMap] = useState([]);
  // itemIndex → ハイライト範囲[] のマップ（searchKeywords または currentPageTextMap 変化時に再計算）
  const [itemHighlights, setItemHighlights] = useState({});

  // 連続検索のレース条件を防ぐ世代カウンタ
  const searchGenRef = useRef(0);

  /**
   * 全ページを走査してキーワードにマッチする位置を収集する。
   * スペース区切りで複数語を指定した場合は OR 検索になる。
   *
   * @param {string} query - 検索クエリ文字列
   */
  const searchInAllPages = useCallback(async (query) => {
    // クエリをスペース区切りで分割して OR 検索用キーワード配列を生成する
    const terms = getSearchKeywords(query);
    if (!pdfDocument || terms.length === 0) {
      setSearchResults([]);
      setCurrentSearchIndex(-1);
      setSearchError(null);
      return;
    }

    // 世代をインクリメントし、この検索の世代番号を記録する
    const gen = ++searchGenRef.current;
    setIsSearching(true);
    setSearchError(null);
    try {
      // 全ページを走査してヒット位置を収集し、ページ・位置順にソートして返す
      const { results, hasAnyText } = await collectAllPageResults(pdfDocument, terms);

      // より新しい検索がすでに走っている場合は結果を捨てる（レース条件防止）
      if (gen !== searchGenRef.current) return;

      setSearchResults(results);

      if (results.length === 0 && !hasAnyText) {
        setSearchError('本文書は文字列検索ができません');
        setCurrentSearchIndex(-1);
      } else {
        // 現在ページ以降で最初にヒットした結果を初期選択とする
        const targetIndex = selectFirstSearchIndex(results, currentPage);
        setCurrentSearchIndex(targetIndex);
        if (targetIndex >= 0) setCurrentPage(results[targetIndex].pageNum);
      }
    } catch (error) {
      console.error('検索エラー:', error);
      setSearchError('検索中にエラーが発生しました');
    } finally {
      setIsSearching(false);
    }
  }, [pdfDocument, currentPage, setCurrentPage]);

  /**
   * 現在の searchText で検索を実行する。
   * UI の検索ボタン・Enterキーから呼ばれる。
   */
  const handleSearch = useCallback(() => {
    searchInAllPages(searchText);
  }, [searchInAllPages, searchText]);

  /**
   * 次の検索結果に移動する。
   * 末尾に達したら先頭に循環する（モジュロ演算で実装）。
   */
  const handleNextSearchResult = useCallback(() => {
    if (searchResults.length === 0) return;
    const nextIndex = (currentSearchIndex + 1) % searchResults.length;
    setCurrentSearchIndex(nextIndex);
    setCurrentPage(searchResults[nextIndex].pageNum);
  }, [searchResults, currentSearchIndex, setCurrentPage]);

  /**
   * 前の検索結果に移動する。
   * 先頭に達したら末尾に循環する（マイナス側のモジュロを三項演算子で実装）。
   */
  const handlePrevSearchResult = useCallback(() => {
    if (searchResults.length === 0) return;
    const prevIndex = currentSearchIndex - 1 < 0 ? searchResults.length - 1 : currentSearchIndex - 1;
    setCurrentSearchIndex(prevIndex);
    setCurrentPage(searchResults[prevIndex].pageNum);
  }, [searchResults, currentSearchIndex, setCurrentPage]);

  /**
   * 検索状態をリセットする。
   * currentSearchIndex を -1（未選択）に戻すことでハイライトもクリアされる。
   */
  const handleClearSearch = useCallback(() => {
    setSearchText('');
    setSearchResults([]);
    setCurrentSearchIndex(-1);
    setSearchError(null);
  }, []);

  /**
   * react-pdf Page の onGetTextSuccess に渡すコールバック。
   * ページのテキストコンテンツ取得完了時に呼ばれ、ハイライト用テキストマップを構築する。
   */
  const onGetTextSuccess = useCallback((textContent) => {
    setCurrentPageTextMap(buildTextMap(textContent.items));
  }, []);

  /**
   * react-pdf Page の onRenderTextLayerSuccess に渡すコールバック。
   * テキストレイヤーの非同期描画が完了した直後（marks が DOM に追加された後）に呼ばれる。
   * textLayerVersion をインクリメントしてオレンジハイライト effect を再実行させる。
   */
  const onTextLayerRenderSuccess = useCallback(() => {
    setTextLayerVersion(v => v + 1);
  }, []);

  /**
   * searchText をスペース区切りで分割した配列（メモ化）。
   * customTextRenderer の itemHighlights 構築に使用する。
   * searchText が空のときは空配列を返してハイライトを無効にする。
   */
  const searchKeywords = useMemo(
    () => getSearchKeywords(searchText),
    [searchText],
  );

  // searchKeywords または currentPageTextMap が変化したらハイライト範囲を再計算する
  useEffect(() => {
    setItemHighlights(buildItemHighlights(currentPageTextMap, searchKeywords));
  }, [searchKeywords, currentPageTextMap]);

  /**
   * react-pdf の Page コンポーネントに渡すテキストレンダラー。
   *
   * アイテムをまたぐキーワードも正確にハイライトするため、
   * buildItemHighlights で事前計算した itemIndex → 範囲[] マップを参照し、
   * アイテム単位で <mark> タグを注入する。
   * DOM操作・setTimeoutを使わないため、ズーム変更後も自動的に再適用される。
   *
   * @param {{ str: string, itemIndex: number }} param - react-pdf から渡される TextItem の文字列とインデックス
   * @returns {string} ハイライト用 <mark> を埋め込んだHTML文字列
   */
  const customTextRenderer = useCallback(
    ({ str, itemIndex }) => applyRangeHighlights(str, itemHighlights[itemIndex]),
    [itemHighlights],
  );

  /**
   * 現在選択中の検索結果をオレンジ色でマークする。
   * CSSクラスのトグルのみで実装（innerHTML 操作なし）。
   *
   * 依存: currentSearchIndex, currentPage, searchResults, containerRef, textLayerVersion
   * textLayerVersion は react-pdf テキストレイヤーの非同期描画完了後にインクリメントされ、
   * marks が DOM に存在しない状態でも描画完了時に再実行される。
   */
  useEffect(() => {
    // containerRef が渡されている場合はそのコンテナ内に限定し、グローバル汚染を防ぐ
    const root = containerRef?.current ?? document;

    // 前回選択時に付与した pdf-search-highlight-current を全て除去する（残留防止）
    root.querySelectorAll('.pdf-search-highlight-current')
      .forEach(el => el.classList.remove('pdf-search-highlight-current'));

    // currentSearchIndex が -1（未選択）またはヒットなしの場合は何もしない
    if (currentSearchIndex < 0 || !searchResults.length) return;

    // 現在ページ内でフォーカス対象の結果の DOM 順インデックスを求める
    const indexInPage = findCurrentResultDomIndex(searchResults, currentSearchIndex, currentPage);
    if (indexInPage < 0) return;

    // data-search-result-index で対象マークを特定し current クラスを付与する
    // クロスアイテムのマークは同じ index を持つため、両方まとめてハイライトされる
    root.querySelectorAll(`.pdf-search-highlight[data-search-result-index="${indexInPage}"]`)
      .forEach(el => el.classList.add('pdf-search-highlight-current'));
  }, [currentSearchIndex, currentPage, searchResults, containerRef, textLayerVersion]);

  return {
    searchText,
    setSearchText,
    searchResults,
    currentSearchIndex,
    isSearching,
    searchError,
    handleSearch,
    handleNextSearchResult,
    handlePrevSearchResult,
    handleClearSearch,
    customTextRenderer,
    onGetTextSuccess,
    onTextLayerRenderSuccess,
  };
};

// #endregion

export default usePdfKeywordSearch;
